import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { APP_DISPLAY_NAME } from '@/lib/appDisplayName'
import { AuthLayout } from '../layouts/AuthLayout'
import { requestPasswordReset } from '../services/authService'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const RESET_EMAIL_STORAGE_KEY = 'shopora_store_admin_reset_email'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const trimmed = email.trim()
    if (!trimmed) {
      setError('Please enter your email address.')
      return
    }
    if (!EMAIL_RE.test(trimmed)) {
      setError('Enter a valid email address.')
      return
    }

    setSubmitting(true)
    try {
      await requestPasswordReset(trimmed)
      sessionStorage.setItem(RESET_EMAIL_STORAGE_KEY, trimmed)
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send reset code.')
    } finally {
      setSubmitting(false)
    }
  }

  const brandInitial = APP_DISPLAY_NAME.charAt(0).toUpperCase() || '—'

  return (
    <AuthLayout>
      <Card accent className="w-full max-w-md shadow-[var(--shadow-modal)]">
        <div className="mb-7 text-center">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-lg font-bold text-primary-foreground shadow-sm">
            {brandInitial}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {sent ? 'Check your inbox' : 'Forgot password?'}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {sent
              ? 'If an account exists for that email, we sent password reset instructions.'
              : 'Enter your store admin email and we will email you a 6-digit reset code.'}
          </p>
        </div>

        {sent ? (
          <div className="space-y-4">
            <p className="rounded-xl border border-border bg-muted/40 px-3 py-3 text-sm text-foreground" role="status">
              Look for an email with a 6-digit reset code. It expires in about 15 minutes.
            </p>
            <Button type="button" variant="outline" className="w-full" asChild>
              <Link to="/reset-password" state={{ email: email.trim() }}>
                Enter reset code
              </Link>
            </Button>
            <Button type="button" variant="outline" className="w-full" disabled={submitting} onClick={handleSubmit}>
              {submitting ? 'Resending…' : 'Resend email'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="font-medium text-foreground underline-offset-4 hover:underline">
                Back to sign in
              </Link>
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Input
              id="email"
              type="email"
              label="Email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {error ? (
              <p className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send reset code'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="font-medium text-foreground underline-offset-4 hover:underline">
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </Card>
    </AuthLayout>
  )
}
